import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { billRefLabel, type BillRef } from '../../../../shared/contracts/bill-ref.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import { personLabel } from '../../components/ApprovalLevels.js';

interface Tiles {
  readonly pendingCommercial: number;
  readonly approvedCommercial: number;
  readonly rejectedCommercial: number;
  readonly returnedCommercial: number;
  readonly duplicateAlerts: number;
  readonly highValueClaims: number;
  readonly overdueReviews: number;
  readonly pendingAccounts: number;
  readonly pendingFinance: number;
  readonly paymentEligible: number;
}

/** The check a forwarded bill waits on next, and the page it is worked on. */
type Stage = 'COMMERCIAL' | 'ACCOUNTS' | 'FINANCE';

/** One bill on Sales Accounts' worklist (GET /v1/approval/dashboard/queue). */
interface QueueItem {
  readonly claimId: string;
  /** The job, dealer and date the bill is known by. */
  readonly bill: BillRef | null;
  readonly vendorName: string | null;
  readonly vendorCode: string | null;
  readonly amount: number;
  readonly stage: Stage;
  /** That check's review, when one is open; null when nobody started it. */
  readonly reviewStatus: string | null;
  readonly forwardedAt: string | null;
}

const TILE: React.CSSProperties = {
  padding: 16, borderRadius: 8,
  border: '1px solid var(--color-border-default, #d0d7de)',
  background: 'var(--color-bg-elevated, #fff)',
  textAlign: 'center', minWidth: 120,
};
const CELL: React.CSSProperties = { padding: 8, borderBottom: '1px solid var(--color-border-default)' };
const NUM: React.CSSProperties = { fontSize: 28, fontWeight: 700, color: 'var(--color-brand-primary, #0969da)' };


export function FinancialApprovalDashboardPage(): ReactNode {
  const { t: tr } = useTranslation();
  // Amounts follow the tenant's DISPLAY_NUMBER_FORMAT, not a locale in the source.
  const { number, money } = useBusinessNumber();
  const { token } = useAuth();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { atDay, day } = useBusinessDay();
  const [tiles, setTiles] = useState<Tiles | null>(null);
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    Promise.all([
      apiRequest<Tiles>('/v1/approval/dashboard/tiles', { token }),
      apiRequest<QueueItem[]>('/v1/approval/dashboard/queue', { token }),
    ])
      .then(([t, q]) => { if (alive) { setTiles(t); setQueue(Array.isArray(q) ? q : []); } })
      .catch(e => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  if (error) return <div style={{ padding: 24 }}><p style={{ color: 'red' }}>{error}</p></div>;
  if (!tiles) return <div style={{ padding: 24 }}>{tr('fa.dash.loading')}</div>;

  const tileData = [
    { label: tr('fa.dash.pendingCommercial'), value: tiles.pendingCommercial, color: 'var(--color-warning)' },
    { label: tr('fa.dash.approved'), value: tiles.approvedCommercial, color: 'var(--color-success)' },
    { label: tr('fa.dash.rejected'), value: tiles.rejectedCommercial, color: 'var(--color-danger)' },
    { label: tr('fa.dash.returned'), value: tiles.returnedCommercial, color: 'var(--color-text-muted)' },
    { label: tr('fa.dash.duplicateAlerts'), value: tiles.duplicateAlerts, color: 'var(--color-danger)' },
    { label: tr('fa.dash.highValueClaims'), value: tiles.highValueClaims, color: 'var(--color-accent-purple)' },
    { label: tr('fa.dash.overdueReviews'), value: tiles.overdueReviews, color: 'var(--color-danger)' },
    { label: tr('fa.dash.pendingAccounts'), value: tiles.pendingAccounts, color: 'var(--color-warning)' },
    { label: tr('fa.dash.pendingFinance'), value: tiles.pendingFinance, color: 'var(--color-warning)' },
    { label: tr('fa.dash.paymentEligible'), value: tiles.paymentEligible, color: 'var(--color-success)' },
  ];

  return (
    <div style={{ padding: 24 }}>
      <h1>{tr('fa.dash.title')}</h1>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}>
        {tileData.map(t => (
          <div key={t.label} style={TILE}>
            <div style={{ ...NUM, color: t.color }}>{number(t.value)}</div>
            <div style={{ fontSize: 13 }}>{t.label}</div>
          </div>
        ))}
      </div>
      <h2>{tr('fa.dash.reviewQueue')}</h2>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>{[tr('fa.dash.colClaim'), tr('fa.dash.colVendor'), tr('fa.dash.colAmount'), tr('fa.dash.colStage'), tr('fa.dash.colStatus'), tr('fa.dash.colForwarded')].map(h => <th key={h} style={{ textAlign: 'left', padding: 8, borderBottom: '2px solid var(--color-border-default)' }}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {queue.map(r => (
            <tr key={r.claimId} data-testid={`worklist-row-${r.claimId}`}>
              <td style={CELL}><Link to={`/financial-approval/claim/${r.claimId}/${r.stage.toLowerCase()}`}>{billRefLabel(r.bill, day)}</Link></td>
              <td style={CELL}>{personLabel(r.vendorName, r.vendorCode) ?? '—'}</td>
              <td style={CELL}>₹{money(r.amount)}</td>
              <td style={CELL}>{tr(`fa.dash.stage.${r.stage}`)}</td>
              <td style={CELL}>{r.reviewStatus ?? tr('fa.dash.notStarted')}</td>
              <td style={CELL}>{r.forwardedAt ? atDay(r.forwardedAt) : '—'}</td>
            </tr>
          ))}
          {queue.length === 0 && <tr><td colSpan={6} style={{ padding: 16, textAlign: 'center', color: 'var(--color-text-muted)' }}>{tr('fa.dash.emptyQueue')}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
